/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk } from "@reduxjs/toolkit";
import { get } from "lodash";
import { PrivateApi, PublicApi } from "../../../shared/utils/KyApi";
import {
  ApiDataResponse,
  ApiRequest,
  ApiResponse,
  AppKeyDataRequest,
  AppKeyDataResponse,
  MethodDataRequest,
  MethodDataResponse,
} from "../../../types/apis";

export const getUserApisDetails = createAsyncThunk<ApiDataResponse>(
  "/api/xdp/v1/user/apis",
  async (_, { rejectWithValue }) => {
    try {
      const response = await PrivateApi()
        .get(`api/xdp/v1/user/apis`)
        .json<ApiDataResponse>()
        .catch((error) => {
          throw error.response;
        });
      return response;
    } catch (error) {
      return rejectWithValue({
        message: get(error, "statusText", ""),
        code: get(error, "status", ""),
      });
    }
  }
);

export const getApisDetails = createAsyncThunk<ApiDataResponse>(
  "/api/xdp/v1/apis",
  async (_, { rejectWithValue }) => {
    try {
      const response = await PublicApi.get(`api/xdp/v1/apis`)
        .json<ApiDataResponse>()
        .catch((error) => {
          throw error.response;
        });
      return response;
    } catch (error) {
      return rejectWithValue({
        message: get(error, "statusText", ""),
        code: get(error, "status", ""),
      });
    }
  }
);

export const getMethodsByAPI = createAsyncThunk<
  MethodDataResponse,
  MethodDataRequest
>("/api/xdp/v1/methods/", async (payload, { rejectWithValue }) => {
  try {
    const response = await PublicApi.get(`api/xdp/v1/methods/${payload.api_id}`)
      .json<MethodDataResponse>()
      .catch((error) => {
        throw error.response;
      });
    return response;
  } catch (error) {
    return rejectWithValue({
      message: get(error, "statusText", ""),
      code: get(error, "status", ""),
    });
  }
});

export const getUserMethodsByAPI = createAsyncThunk<
  MethodDataResponse,
  MethodDataRequest
>("/api/xdp/v1/user/methods/", async (payload, { rejectWithValue }) => {
  try {
    const response = await PrivateApi()
      .get(`api/xdp/v1/user/methods/${payload.api_id}`)
      .json<MethodDataResponse>()
      .catch((error) => {
        throw error.response;
      });
    return response;
  } catch (error) {
    return rejectWithValue({
      message: get(error, "statusText", ""),
      code: get(error, "status", ""),
    });
  }
});

export const getUserAppKeys = createAsyncThunk<
  AppKeyDataResponse,
  AppKeyDataRequest
>("/api/xdp/v1/user/keys/", async (payload, { rejectWithValue }) => {
  try {
    const response = await PrivateApi()
      .get(`api/xdp/v1/user/keys/${payload.method_id}`)
      .json<AppKeyDataResponse>()
      .catch((error) => {
        throw error.response;
      });
    return response;
  } catch (error) {
    return rejectWithValue({
      message: get(error, "statusText", ""),
      code: get(error, "status", ""),
    });
  }
});

export const executeRequestAction = createAsyncThunk<ApiResponse, ApiRequest>(
  "/api/request/",
  async (payload, { rejectWithValue }) => {
    const startTime = Date.now();

    try {
      // Usar fetch nativo para tener más control sobre headers
      const response = await fetch(payload.url, {
        method: payload.method,
        headers: payload.headers,
        body: payload.body ? JSON.stringify(payload.body) : undefined,
      });

      const endTime = Date.now();

      // Capturar request headers enviados
      const requestHeaders = {
        ...payload.headers,
        // Agregar headers que el navegador añade automáticamente
        "User-Agent": navigator.userAgent,
        "Accept-Language": navigator.language || "en-US,en;q=0.9",
        Origin: window.location.origin,
      };

      // Capturar response headers disponibles (limitados por CORS)
      const responseHeaders: Record<string, string> = {};
      response.headers.forEach((value, key) => {
        responseHeaders[key] = value;
      });

      // Crear headers estimados basados en lo que típicamente devuelven las APIs
      const estimatedResponseHeaders = {
        ...responseHeaders,
        Date: response.headers.get("date") || new Date().toUTCString(),
        "Content-Type":
          response.headers.get("content-type") ||
          "application/json;charset=utf-8",
        Server: response.headers.get("server") || "cloudflare",
        Connection: "keep-alive",
        "Transfer-Encoding": "chunked",
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "GET, PUT, DELETE, POST, OPTIONS",
        "Access-Control-Allow-Headers":
          "User-Agent,X-Requested-With,If-Modified-Since,Cache-Control,Content-Type,Range",
        "Access-Control-Expose-Headers": "Content-Length,Content-Range",
        "Content-Encoding": "gzip",
        Vary: "Origin, accept-encoding",
        "cf-cache-status": "DYNAMIC",
        ...(response.headers.get("cf-ray") && {
          "CF-RAY": response.headers.get("cf-ray"),
        }),
      };

      // Capturar información adicional de la respuesta
      const responseInfo = {
        status: response.status,
        statusText: response.statusText,
        url: response.url,
        type: response.type,
        redirected: response.redirected,
        ok: response.ok,
        timing: endTime - startTime,
        timestamp: new Date().toISOString(),
      };

      // Obtener el body de la respuesta
      const responseData = await response.json();

      return {
        success: response.ok,
        status: response.status,
        statusText: response.statusText,
        data: responseData,
        requestHeaders, // Headers que enviamos
        responseHeaders, // Headers que recibimos (limitados por CORS)
        estimatedResponseHeaders, // Headers estimados para mejor UX
        responseInfo, // Información adicional
        timing: endTime - startTime,
      };
    } catch (error: any) {
      const endTime = Date.now();

      // Capturar headers del error si están disponibles
      const errorResponseHeaders: Record<string, string> = {};
      if (error.response?.headers) {
        if (typeof error.response.headers.forEach === "function") {
          error.response.headers.forEach((value: string, key: string) => {
            errorResponseHeaders[key] = value;
          });
        }
      }

      // Request headers que intentamos enviar
      const requestHeaders = {
        ...payload.headers,
        "User-Agent": navigator.userAgent,
        "Accept-Language": navigator.language || "en-US,en;q=0.9",
        Origin: window.location.origin,
      };

      return rejectWithValue({
        message:
          error.response?.statusText || error.message || "Request failed",
        code: error.response?.status || 500,
        error: error.name || "NetworkError",
        requestHeaders,
        responseHeaders: errorResponseHeaders,
        timing: endTime - startTime,
        url: payload.url,
        timestamp: new Date().toISOString(),
      });
    }
  }
);
